import { useRef } from 'react'

import { ProgressBar } from '@/components/ProgressBar'
import type { TgeOutcome, TgeRunSummary } from '@/lib/tge'

import { OutcomeText } from './TgeReports'

function clock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  const m = Math.floor(s / 60)
  return `${m}:${String(s % 60).padStart(2, '0')}`
}

function left(seconds: number): string {
  if (seconds < 60) return 'under a minute left'
  return `~${Math.round(seconds / 60)} min left`
}

/**
 * Progress of a running migration, from what the run writes to its row as it goes:
 * reading the grid → resolving keys → writing (or checking, on a dry run) issue by issue.
 * Time left is measured in the writing stage only — reading the grid runs at its own pace.
 */
export function RunProgress({ run }: { run: TgeRunSummary }) {
  const p = run.progress
  // first writing sample of this run: the rate is measured from it
  const firstRef = useRef<{ at: number; done: number } | null>(null)
  const now = Date.now()
  const elapsed = (now - new Date(run.started_at).getTime()) / 1000
  const verb = run.dry_run ? 'Checking' : 'Writing to STAGIL'

  let bar: { value?: number; indeterminate?: boolean; label: string }
  if (p === null) {
    bar = { indeterminate: true, label: `Run #${run.id} starting…` }
  } else if (p.stage === 'reading') {
    bar = p.total
      ? { value: Math.min(100, (p.done / p.total) * 100), label: `Reading the TGE grid from Jira DC — ${p.done} of ~${p.total} issues` }
      : { indeterminate: true, label: `Reading the TGE grid from Jira DC — ${p.done} issues so far` }
  } else if (p.stage === 'resolving') {
    bar = { indeterminate: true, label: `Resolving issue keys on Jira DC — ${p.done ? `${p.done} of ` : ''}${p.total ?? '?'} issues` }
  } else {
    const total = p.total ?? 0
    let eta = ''
    if (p.stage === 'writing') {
      if (firstRef.current === null) firstRef.current = { at: now, done: p.done }
      const first = firstRef.current
      const rate = (p.done - first.done) / ((now - first.at) / 1000)
      if (rate > 0 && p.done < total) eta = ` · ${left((total - p.done) / rate)}`
    }
    bar = {
      value: total ? (p.done / total) * 100 : 0,
      label: p.stage === 'done'
        ? `Finishing — ${total} issues`
        : `${verb} — ${p.done} of ${total} issues${eta}`,
    }
  }

  const outcomes = Object.entries(p?.outcomes ?? {}) as [TgeOutcome, number][]
  return (
    <div className="space-y-2">
      <ProgressBar {...bar} />
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 font-mono text-[11.5px] text-text-3">
        <span>elapsed {clock(elapsed)}</span>
        {outcomes.map(([o, n]) => (
          <span key={o} className="inline-flex items-baseline gap-1">
            <span className="text-[12.5px] text-foreground">{n}</span>
            <OutcomeText outcome={o} />
          </span>
        ))}
      </div>
    </div>
  )
}
